import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TaskSubtasks from './TaskSubtasks';
import {
  ChevronDown, Check, Hash, X, Plus, Flag, Circle, CalendarDays, Clock, Bell,
  FolderKanban, Pencil, Share2, Users2, StickyNote, MessageSquare, Repeat, Bookmark, Eye,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// TaskInlineEditor — an expandable edit panel shown inside a list row or a
// kanban card so users can change project, tags, due date, reminders, sharing
// and sub-tasks WITHOUT opening the detail panel. (Notes & comments still live
// in the detail panel — a shortcut button jumps there.)
//
// Fully controlled by the `task` prop: every change calls `onPatch(patch)`
// (which updates the parent list), so the panel re-renders with fresh values.
// ---------------------------------------------------------------------------

const RECUR = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'daily', label: 'Every day' },
  { value: 'weekly', label: 'Every week' },
  { value: 'monthly', label: 'Every month' },
  { value: 'annually', label: 'Every year' },
];
// Task repeat (distinct wording from the reminder recurrence above).
const RECUR_TASK = [
  { value: 'none', label: "Doesn't repeat" },
  { value: 'daily', label: 'Repeat daily' },
  { value: 'weekly', label: 'Repeat weekly' },
  { value: 'monthly', label: 'Repeat monthly' },
  { value: 'annually', label: 'Repeat yearly' },
  { value: 'weekdays', label: 'Repeat on days…' },
];
// Weekday repeat chips (Mon=0 … Sun=6 — matches backend datetime.weekday()).
const WEEKDAY_CHIPS = [
  { v: 0, label: 'M', name: 'Monday' }, { v: 1, label: 'T', name: 'Tuesday' },
  { v: 2, label: 'W', name: 'Wednesday' }, { v: 3, label: 'T', name: 'Thursday' },
  { v: 4, label: 'F', name: 'Friday' }, { v: 5, label: 'S', name: 'Saturday' },
  { v: 6, label: 'S', name: 'Sunday' },
];
const defaultWeekdayFor = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const base = Number.isNaN(d.getTime()) ? new Date() : d;
  return (base.getDay() + 6) % 7;
};
const RECUR_END = [
  { value: 'never', label: 'Never ends' },
  { value: 'on', label: 'On date' },
  { value: 'after', label: 'After…' },
];
const STATUS_OPTS = [
  { value: 'todo', label: 'To do', dot: 'var(--n-muted-2)' },
  { value: 'in_progress', label: 'In progress', dot: 'var(--n-accent-2)' },
  { value: 'done', label: 'Done', dot: 'var(--n-accent)' },
];
const PRIORITIES = [
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Normal' },
];

const pad = (n) => String(n).padStart(2, '0');
const toLocalDate = (iso) => { if (!iso) return ''; const d = new Date(iso); return isNaN(d) ? '' : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
const toLocalTime = (iso) => { if (!iso) return ''; const d = new Date(iso); return isNaN(d) ? '' : `${pad(d.getHours())}:${pad(d.getMinutes())}`; };
const endOfDayIso = (dateStr) => { if (!dateStr) return null; const d = new Date(`${dateStr}T00:00:00`); if (isNaN(d)) return null; d.setHours(23, 59, 59, 999); return d.toISOString(); };
const combineIso = (dateStr, timeStr) => { if (!dateStr) return null; const d = new Date(`${dateStr}T${timeStr || '09:00'}`); return isNaN(d) ? null : d.toISOString(); };

const statusOf = (t) => t.status || (t.is_completed ? 'done' : 'todo');

const useOutside = (ref, onClose) => {
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose(); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref, onClose]);
};

// Lightweight select dropdown matching the notes design tokens.
const InlineSelect = ({ value, onChange, options, disabled, testid, leading = null, width = 'w-full' }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOutside(ref, () => setOpen(false));
  const cur = options.find((o) => o.value === value) || options[0];
  return (
    <div className={`relative ${width}`} ref={ref}>
      <button type="button" data-testid={testid} disabled={disabled} onClick={() => setOpen((o) => !o)}
        className="n-focus flex w-full items-center gap-1.5 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-1.5 text-[12.5px] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] disabled:opacity-60">
        {leading}<span className="flex-1 truncate text-left">{cur?.label}</span><ChevronDown size={13} className="shrink-0 text-[var(--n-muted-2)]" />
      </button>
      {open && !disabled && (
        <div className="absolute left-0 z-[70] mt-1 max-h-60 w-full min-w-[160px] overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]">
          {options.map((o) => (
            <button key={o.value} type="button" data-testid={`${testid}-opt-${o.value}`} onClick={() => { onChange(o.value); setOpen(false); }}
              className={`flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] transition-colors hover:bg-[var(--n-canvas-2)] ${value === o.value ? 'text-[var(--n-accent)]' : 'text-[var(--n-ink-soft)]'}`}>
              {o.dot && <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: o.dot }} />}
              <span className="flex-1 truncate">{o.label}</span>
              {value === o.value && <Check size={12} className="shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Tag adder with suggestions.
const InlineTagAdder = ({ existing, options, onAdd }) => {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState('');
  const ref = useRef(null);
  useOutside(ref, () => { setOpen(false); setVal(''); });
  const avail = options.filter((o) => !existing.includes(o) && o.toLowerCase().includes(val.trim().toLowerCase()));
  const add = (t) => { const c = (t || '').trim(); if (!c) return; onAdd(c); setVal(''); setOpen(false); };
  return (
    <div className="relative" ref={ref}>
      <button type="button" data-testid="task-inline-tag-add" onClick={() => setOpen((o) => !o)}
        className="n-focus inline-flex items-center gap-1 rounded-full border border-dashed border-[var(--n-border)] px-2 py-0.5 text-[12px] text-[var(--n-muted)] hover:border-[var(--n-accent)] hover:text-[var(--n-accent)]">
        <Plus size={11} /> add tag
      </button>
      {open && (
        <div className="absolute left-0 z-[70] mt-1 w-56 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1.5 shadow-[var(--n-shadow)]">
          <input autoFocus value={val} placeholder="Type a tag, Enter to add…" data-testid="task-inline-tag-input"
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') add(val); else if (e.key === 'Escape') { setOpen(false); setVal(''); } }}
            className="n-focus mb-1 w-full rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1.5 text-[13px] text-[var(--n-ink)] placeholder:text-[var(--n-muted-2)]" />
          <div className="max-h-40 overflow-y-auto n-scroll">
            {avail.slice(0, 20).map((o) => (
              <button key={o} type="button" data-testid="task-inline-tag-suggestion" onClick={() => add(o)}
                className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
                <Hash size={11} className="text-[var(--n-muted-2)]" />{o}
              </button>
            ))}
            {val.trim() && !options.map((x) => x.toLowerCase()).includes(val.trim().toLowerCase()) && (
              <button type="button" onClick={() => add(val)} className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] text-[var(--n-accent)] hover:bg-[var(--n-canvas-2)]">
                <Plus size={11} /> Create “{val.trim()}”
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Project picker with inline rename for owned projects.
const ProjectPicker = ({ value, projects, canEdit, onSelect, onRename }) => {
  const [open, setOpen] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameVal, setRenameVal] = useState('');
  const ref = useRef(null);
  useOutside(ref, () => { setOpen(false); setRenamingId(null); });
  const cur = projects.find((p) => p.id === value);
  const submitRename = (pid) => { const nm = renameVal.trim(); setRenamingId(null); if (nm) onRename(pid, nm); };
  return (
    <div className="relative w-full" ref={ref}>
      <button type="button" data-testid="task-inline-project" disabled={!canEdit} onClick={() => setOpen((o) => !o)}
        className="n-focus flex w-full items-center gap-1.5 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-1.5 text-[12.5px] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)] disabled:opacity-60">
        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: cur?.color || 'var(--n-accent)' }} />
        <span className="flex-1 truncate text-left">{cur?.name || 'No project'}</span>
        <ChevronDown size={13} className="shrink-0 text-[var(--n-muted-2)]" />
      </button>
      {open && canEdit && (
        <div className="absolute left-0 z-[70] mt-1 max-h-64 w-64 overflow-y-auto n-scroll rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-1 shadow-[var(--n-shadow)]" data-testid="task-inline-project-menu">
          {projects.map((p) => (
            <div key={p.id} className="flex items-center gap-1">
              {renamingId === p.id ? (
                <input autoFocus value={renameVal} data-testid="task-inline-project-rename-input"
                  onChange={(e) => setRenameVal(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') submitRename(p.id); else if (e.key === 'Escape') setRenamingId(null); }}
                  onBlur={() => submitRename(p.id)}
                  className="n-focus my-0.5 min-w-0 flex-1 rounded-md border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1 text-[13px] text-[var(--n-ink)]" />
              ) : (
                <button type="button" data-testid="task-inline-project-option" onClick={() => { onSelect(p.id); setOpen(false); }}
                  className={`flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] transition-colors hover:bg-[var(--n-canvas-2)] ${value === p.id ? 'text-[var(--n-accent)]' : 'text-[var(--n-ink-soft)]'}`}>
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: p.color || 'var(--n-accent)' }} />
                  <span className="flex-1 truncate">{p.name}</span>
                  {value === p.id && <Check size={12} className="shrink-0" />}
                </button>
              )}
              {!p.shared_by && renamingId !== p.id && (
                <button type="button" data-testid="task-inline-project-rename" title="Rename project"
                  onClick={() => { setRenamingId(p.id); setRenameVal(p.name); }}
                  className="n-focus grid h-7 w-7 shrink-0 place-items-center rounded-md text-[var(--n-muted-2)] hover:text-[var(--n-accent)]"><Pencil size={12} /></button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Label = ({ children }) => (
  <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-[var(--n-muted-2)]">{children}</div>
);

const TaskInlineEditor = ({
  task, canEdit = false, projects = [], availableTags = [],
  onPatch, onRenameProject, onShare, onOpenDetail, onSubtaskSync, onSaveTemplate,
}) => {
  const [title, setTitle] = useState(task.title || '');
  useEffect(() => { setTitle(task.title || ''); }, [task.title]);

  const patch = useCallback((p) => { if (onPatch) onPatch(p); }, [onPatch]);
  const tags = task.tags || [];
  const shared = task.shared_with || [];

  const submitTitle = () => {
    const t = (title || '').trim();
    if (!t || t === task.title) { setTitle(task.title || ''); return; }
    patch({ title: t });
  };

  const dueDate = toLocalDate(task.due_at);
  const remDate = toLocalDate(task.reminder_at);
  const remTime = toLocalTime(task.reminder_at);

  const projectOptions = useMemo(() => projects, [projects]);

  return (
    <div data-testid="task-inline-editor"
      className="mt-1 rounded-xl border border-[var(--n-border)] bg-[var(--n-surface)] p-3 shadow-[var(--n-shadow)]">
      {/* Title */}
      <input value={title} disabled={!canEdit} data-testid="task-inline-title"
        onChange={(e) => setTitle(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
        onBlur={submitTitle}
        className="n-focus mb-3 w-full rounded-lg border border-transparent bg-transparent px-1 py-1 text-[15px] font-semibold text-[var(--n-ink)] hover:border-[var(--n-border)] focus:border-[var(--n-border)] focus:bg-[var(--n-canvas-2)] disabled:opacity-70" />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {/* Priority */}
        <div>
          <Label>Priority</Label>
          <div className="inline-flex rounded-lg border border-[var(--n-border)] p-0.5" data-testid="task-inline-priority">
            {PRIORITIES.map((p) => (
              <button key={p.value} type="button" data-testid={`task-inline-priority-${p.value}`} disabled={!canEdit}
                onClick={() => patch({ priority: p.value })}
                className={`n-focus rounded-md px-2 py-1 text-[12px] font-medium transition-colors ${(task.priority || 'low') === p.value ? 'bg-[var(--n-accent-bg)] text-[var(--n-accent)]' : 'text-[var(--n-muted)] hover:text-[var(--n-ink)]'}`}>
                {p.value === 'high' ? <Flag size={11} className="mr-1 inline" /> : p.value === 'medium' ? <Circle size={7} className="mr-1 inline fill-current" /> : null}{p.label}
              </button>
            ))}
          </div>
        </div>

        {/* Status */}
        <div>
          <Label>Status</Label>
          <InlineSelect testid="task-inline-status" disabled={!canEdit} value={statusOf(task)}
            onChange={(v) => patch({ status: v })} options={STATUS_OPTS}
            leading={<span className="h-2 w-2 shrink-0 rounded-full" style={{ background: (STATUS_OPTS.find((s) => s.value === statusOf(task)) || {}).dot }} />} />
        </div>

        {/* Due date */}
        <div>
          <Label>Due date</Label>
          <div className="flex items-center gap-1.5">
            <CalendarDays size={14} className="shrink-0 text-[var(--n-muted-2)]" />
            <input type="date" value={dueDate} disabled={!canEdit} data-testid="task-inline-due-date"
              onChange={(e) => patch({ due_at: e.target.value ? endOfDayIso(e.target.value) : null })}
              className="n-focus flex-1 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1.5 text-[12.5px] text-[var(--n-ink)]" />
            {dueDate && canEdit && (
              <button type="button" data-testid="task-inline-due-clear" onClick={() => patch({ due_at: null })}
                className="n-focus grid h-7 w-7 place-items-center rounded-md text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><X size={13} /></button>
            )}
          </div>
        </div>

        {/* Repeat — sits next to Due date (recurrence type + end date / after-N) */}
        <div>
          <Label>Repeat</Label>
          <div className="flex flex-wrap items-center gap-1.5" data-testid="task-inline-repeat">
            <Repeat size={14} className="shrink-0 text-[var(--n-muted-2)]" />
            <InlineSelect testid="task-inline-recur" disabled={!canEdit} width="w-40"
              value={task.recurrence || 'none'} onChange={(v) => {
                if (v === 'weekdays') {
                  const cur = task.recurrence_weekdays || [];
                  patch({ recurrence: 'weekdays', recurrence_weekdays: cur.length ? cur : [defaultWeekdayFor(task.due_at)] });
                } else { patch({ recurrence: v }); }
              }} options={RECUR_TASK} />
            {task.recurrence === 'weekdays' && (
              <span className="inline-flex flex-wrap gap-1" data-testid="task-inline-weekdays">
                {WEEKDAY_CHIPS.map((wd) => {
                  const on = (task.recurrence_weekdays || []).includes(wd.v);
                  return (
                    <button key={wd.v} type="button" disabled={!canEdit} data-testid={`task-inline-weekday-${wd.v}`}
                      aria-pressed={on} title={wd.name}
                      onClick={() => {
                        const cur = new Set(task.recurrence_weekdays || []);
                        if (on) { if (cur.size <= 1) return; cur.delete(wd.v); } else cur.add(wd.v);
                        patch({ recurrence: 'weekdays', recurrence_weekdays: [...cur].sort((a, b) => a - b) });
                      }}
                      className={`n-focus grid h-7 w-7 place-items-center rounded-full border text-[11.5px] font-semibold transition-colors disabled:opacity-50 ${on ? 'border-[var(--n-accent)] bg-[var(--n-accent)] text-white' : 'border-[var(--n-border)] text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]'}`}>
                      {wd.label}
                    </button>
                  );
                })}
              </span>
            )}
            {task.recurrence && task.recurrence !== 'none' && (
              <>
                <span className="text-[11.5px] text-[var(--n-muted-2)]">ends</span>
                <InlineSelect testid="task-inline-recur-end-mode" disabled={!canEdit} width="w-32"
                  value={task.recurrence_end_mode || 'never'} onChange={(v) => patch({ recurrence_end_mode: v })} options={RECUR_END} />
                {task.recurrence_end_mode === 'on' && (
                  <input type="date" data-testid="task-inline-recur-end-date" disabled={!canEdit}
                    value={task.recurrence_end_date ? toLocalDate(task.recurrence_end_date) : ''}
                    onChange={(e) => patch({ recurrence_end_date: e.target.value ? new Date(`${e.target.value}T00:00:00`).toISOString() : null })}
                    className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1.5 text-[12.5px] text-[var(--n-ink)]" />
                )}
                {task.recurrence_end_mode === 'after' && (
                  <span className="inline-flex items-center gap-1.5">
                    <input type="number" min="1" max="365" data-testid="task-inline-recur-count" disabled={!canEdit}
                      value={task.recurrence_count || ''}
                      onChange={(e) => patch({ recurrence_count: e.target.value ? Math.max(1, parseInt(e.target.value, 10) || 1) : null })}
                      className="n-focus w-16 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1.5 text-[12.5px] text-[var(--n-ink)]" />
                    <span className="text-[12px] text-[var(--n-muted)]">times</span>
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Project — on its own line, below Due date / Repeat */}
      <div className="mt-3">
        <Label>Project</Label>
        <ProjectPicker value={task.project_id} projects={projectOptions} canEdit={canEdit}
          onSelect={(pid) => patch({ project_id: pid })} onRename={onRenameProject} />
      </div>

      {/* Reminder */}
      <div className="mt-3">
        <Label>Reminder</Label>
        <div className="flex flex-wrap items-center gap-1.5">
          <Bell size={14} className="shrink-0 text-[var(--n-muted-2)]" />
          <input type="date" value={remDate} disabled={!canEdit} data-testid="task-inline-reminder-date"
            onChange={(e) => patch({ reminder_at: e.target.value ? combineIso(e.target.value, remTime) : null })}
            className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1.5 text-[12.5px] text-[var(--n-ink)]" />
          <input type="time" step="60" value={remTime} disabled={!canEdit || !remDate} data-testid="task-inline-reminder-time"
            onChange={(e) => patch({ reminder_at: combineIso(remDate, e.target.value) })}
            className="n-focus rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2 py-1.5 text-[12.5px] text-[var(--n-ink)] disabled:opacity-60" />
          <InlineSelect testid="task-inline-reminder-recur" disabled={!canEdit || !remDate} width="w-40"
            value={task.reminder_rule || 'none'} onChange={(v) => patch({ reminder_rule: v })} options={RECUR} />
          {remDate && canEdit && (
            <button type="button" data-testid="task-inline-reminder-clear" onClick={() => patch({ reminder_at: null, reminder_rule: 'none' })}
              className="n-focus grid h-7 w-7 place-items-center rounded-md text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><X size={13} /></button>
          )}
        </div>
      </div>

      {/* Tags */}
      <div className="mt-3">
        <Label>Tags</Label>
        <div className="flex flex-wrap items-center gap-1.5" data-testid="task-inline-tags">
          {tags.map((t) => (
            <span key={t} className="group inline-flex items-center gap-1 rounded-full border border-[var(--n-border)] px-2 py-0.5 text-[12px] text-[var(--n-ink-soft)]">
              <Hash size={10} />{t}
              {canEdit && <button type="button" data-testid="task-inline-tag-remove" onClick={() => patch({ tags: tags.filter((x) => x !== t) })} className="n-focus text-[var(--n-muted-2)] hover:text-[var(--n-danger)]"><X size={11} /></button>}
            </span>
          ))}
          {canEdit && <InlineTagAdder existing={tags} options={availableTags} onAdd={(t) => patch({ tags: [...tags, t] })} />}
          {tags.length === 0 && !canEdit && <span className="text-[12px] text-[var(--n-muted-2)]">No tags</span>}
        </div>
      </div>

      {/* Sharing */}
      <div className="mt-3">
        <Label>Sharing</Label>
        <div className="flex flex-wrap items-center gap-2" data-testid="task-inline-sharing">
          {shared.length > 0 ? (
            <div className="flex items-center -space-x-1.5">
              {shared.slice(0, 5).map((p, i) => (
                <span key={p.id || i} title={p.name || p.email}
                  className="grid h-6 w-6 place-items-center rounded-full border-2 border-[var(--n-surface)] bg-[var(--n-accent)] text-[10px] font-bold text-white">
                  {(p.name || p.email || 'U').charAt(0).toUpperCase()}
                </span>
              ))}
              {shared.length > 5 && <span className="ml-2 text-[11px] text-[var(--n-muted)]">+{shared.length - 5}</span>}
            </div>
          ) : (
            <span className="inline-flex items-center gap-1 text-[12px] text-[var(--n-muted-2)]"><Users2 size={12} /> Not shared yet</span>
          )}
          {!task.shared_by ? (
            <button type="button" data-testid="task-inline-share" onClick={onShare}
              className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-1 text-[12px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
              <Share2 size={13} /> Manage sharing
            </button>
          ) : !canEdit ? (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-[var(--n-canvas-2)] px-2 py-1 text-[11.5px] text-[var(--n-muted)]" data-testid="task-inline-viewonly-badge">
              <Eye size={12} /> View only — you can tick it complete, not change details
            </span>
          ) : null}
          {onSaveTemplate && (
            <button type="button" data-testid="task-inline-save-template" onClick={onSaveTemplate}
              title="Save this task as a reusable template"
              className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-1 text-[12px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
              <Bookmark size={13} /> Save as template
            </button>
          )}
        </div>
      </div>

      {/* Sub-tasks */}
      <div className="mt-3 border-t border-[var(--n-line)] pt-3">
        <TaskSubtasks key={`inline-sub-${task.id}`} taskId={task.id} canEdit={canEdit}
          onTaskSync={onSubtaskSync} />
      </div>

      {/* Jump to detail for notes & comments */}
      <div className="mt-3 flex items-center gap-2 border-t border-[var(--n-line)] pt-3">
        <button type="button" data-testid="task-inline-open-notes" onClick={onOpenDetail}
          className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-1.5 text-[12px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
          <StickyNote size={13} /> Notes
        </button>
        <button type="button" data-testid="task-inline-open-comments" onClick={onOpenDetail}
          className="n-focus inline-flex items-center gap-1.5 rounded-lg border border-[var(--n-border)] bg-[var(--n-surface)] px-2.5 py-1.5 text-[12px] font-medium text-[var(--n-ink-soft)] hover:bg-[var(--n-canvas-2)]">
          <MessageSquare size={13} /> Comments
        </button>
        <span className="text-[11px] text-[var(--n-muted-2)]">Notes &amp; comments open in the detail panel</span>
      </div>
    </div>
  );
};

export default TaskInlineEditor;
